<template>
  <section v-if="hasMainlineResults" class="mainline" data-test-id="mainline">
    <EMetaResults v-if="!hasFullWidthProductAds" />
    <div>
      <template v-for="(pageResults, pageIndex) in mainlineResults">
        <EDivider
          v-if="pageIndex > 0"
          :key="`result-divider-${pageIndex}`"
          class="mainline__divider"
        />
        <template
          v-for="(resultContainer, resultIndex) in mapResults(pageResults)"
        >
          <div
            v-if="resultContainer.component"
            :key="`result-${pageIndex}-${resultIndex}`"
            class="mainline__result-wrapper"
            :data-test-id="`mainline-result-${resultContainer.result.type}`"
          >
            <component
              :is="resultContainer.component"
              :ref="getResultRef(pageIndex, resultIndex)"
              class="mainline__result"
              v-bind="resultContainer.props"
              @ad-click="onAdClick(resultContainer.result)"
            />
          </div>
        </template>
      </template>
    </div>
    <ERelatedQueries
      :query="query"
      class="mainline__result"
      data-test-id="web-related-queries"
    />
    <ESearchShortcutsInfoBox :position="'bottom'" />
    <SatisfactionFeedback v-if="renderSFE" />
    <div class="mainline__pagination">
      <EDynamicPagination
        v-if="dynamicPagination"
        data-test-id="mainline-pagination"
        @change="fetchNextResults"
      />
      <EStaticPagination
        v-else-if="staticPagination"
        :current="page"
        :pages="pagination.totalNumberOfPages"
        data-test-id="web-pagination"
      />
    </div>
    <EStatusMessage
      v-if="fetchError"
      icon="exclamation-circle"
      class="mainline__error"
    >
      <template #title>
        {{ $t('common.generic.error') }}
      </template>
      <template #message>
        {{ $t('common.generic.tryagain') }}
      </template>
    </EStatusMessage>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { focusFocusableChild } from '~~/common/js/client/keyboard-utils';
import EDivider from '~~/common/vue/components/divider';
import ERelatedQueries from '~/components/related-queries';
import EStaticPagination from '~~/common/vue/components/pagination';
import EStatusMessage from '~~/common/vue/components/status-message';
import screen from '~~/common/vue/mixins/screen';
import EQueryCorrection from '~/components/query-correction';
import ESpellingSuggestion from '~/components/spelling-suggestion';
import EDynamicPagination from '~/components/dynamic-pagination';
import EMetaResults from '~/components/meta-results';
import mapResults from '~/results/map';
import { MAINLINE } from '~/results/position';
import SatisfactionFeedback from '~/components/satisfaction-feedback';
import ESearchShortcutsInfoBox from '~/components/search-shortcuts-info-box';
import { trackImpressions, getTrackingProperties } from '~~/search/common/js/client/analytics/results';

const resultRef = 'result';
const resultRefLastPageFirst = 'result-last-page-first';

export default {
  components: {
    EQueryCorrection,
    ESpellingSuggestion,
    ERelatedQueries,
    EStaticPagination,
    EDynamicPagination,
    EDivider,
    EStatusMessage,
    EMetaResults,
    SatisfactionFeedback,
    ESearchShortcutsInfoBox,
  },
  mixins: [screen],
  props: {
    hasFullWidthProductAds: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      renderSFE: false,
    };
  },
  computed: {
    ...mapState([
      'fetchError',
      'mainlineResults',
      'pagination',
      'queryCorrection',
      'spellingSuggestion',
    ]),
    ...mapGetters([
      'dynamicPagination',
      'hasMainlineResults',
      'staticPagination',
    ]),
    ...mapGetters('unleash', ['getFeatureToggle']),
    query() {
      return this.$route.query.q?.toString();
    },
    page() {
      return this.$route.query.p ? parseInt(this.$route.query.p, 10) : 0;
    },
    isDeskTopAbove() {
      return this.isDesktopUp;
    },
    trackingOptions() {
      const trackingEntries = [];

      // in order to get all the meta information and the correct data structure
      const mainLineResults =
        this.mainlineResults.reduce((acc, results) => [...acc, ...this.mapResults(results)], []);

      mainLineResults
        .forEach(({ result, props }) => getTrackingProperties(result, props, trackingEntries));

      return {
        query: this.query,
        impressions: trackingEntries,
        page_position: MAINLINE,
      };
    },
  },
  watch: {
    isDeskTopAbove(newValue, oldValue) {
      if (!newValue && newValue !== oldValue && this.page <= 0) {
        this.renderSFE = true;
      }
    },
  },
  mounted() {
    this.unwatch = this.$watch('mainlineResults', this.onNewResults.bind(this));
    if (this.hasMainlineResults) {
      this.$track(trackImpressions(this.trackingOptions));
    }
  },
  beforeDestroy() {
    if (this.unwatch) {
      this.unwatch();
    }
  },
  methods: {
    ...mapActions(['fetchNextResults', 'trackAdClick']),
    onNewResults() {
      this.$nextTick(() => {
        // this ensures the user can continue navigating
        // through the results in the right order
        const firstResultOnLastPage = this.$refs[resultRefLastPageFirst]?.[0]?.$el;
        if (firstResultOnLastPage) {
          focusFocusableChild(firstResultOnLastPage);
          firstResultOnLastPage.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
    },
    getResultRef(pageIndex, resultIndex) {
      const isLastPage = pageIndex === this.mainlineResults.length - 1;
      const isLastPageFirstResult = isLastPage && resultIndex === 0;
      return isLastPageFirstResult ? resultRefLastPageFirst : resultRef;
    },
    onAdClick({ pingSuffix }) {
      this.trackAdClick(pingSuffix);
    },
    mapResults(results) {
      return mapResults(results, MAINLINE, this.getFeatureToggle);
    },
  },
};
</script>
<style lang="scss" scoped>
.mainline__divider {
  margin: $space-m;

  @include desktop {
    margin: $space-l $space-m $space-1l;
  }
}

.mainline__pagination {
  background: var(--color-background-primary);
  padding-top: $space-1l;

  @include desktop {
    padding-top: $space-4l;
  }
}

.mainline__error {
  justify-content: center;
}

.mainline__result-wrapper {
  background: var(--color-background-primary);
}

.mainline__result {
  margin-bottom: $space-1s;

  // special case, we don't want a gap between related queries and pagination
  &.related-queries {
    margin-bottom: 0;
  }
}
</style>
